/**
 * Daaluu's manual table preparation. Pure: the server supplies hidden tile IDs,
 * independent public handles, round IDs and RNG, and owns all deadlines.
 * This module deliberately does not guess tile faces or scoring rules.
 */
import type { Rng } from './cards.js';

export type DaaluuSeats = 5;
export type DaaluuStackSize = 5;
export interface DaaluuPoint { x: number; y: number }
export interface DaaluuTableTile extends DaaluuPoint { handle: string; angle: number }
export interface DaaluuGesture extends DaaluuPoint { seat: number; angle: number }
export interface DaaluuShuffleCommand extends DaaluuPoint {
  roundId: string;
  attempt: number;
  sequence: number;
  contact: boolean;
}
export interface DaaluuShuffleView {
  roundId: string;
  attempt: number;
  stage: 'mixing' | 'retry' | 'ready';
  callerSeat: number;
  seats: DaaluuSeats;
  stackSize: DaaluuStackSize;
  reserveCount: number;
  tiles: DaaluuTableTile[];
  gestures: DaaluuGesture[];
  participatedSeats: number[];
  missingSeats: number[];
  acceptedSequences: number[];
  stacks: { handle: string; count: number }[];
}
interface HiddenTile extends DaaluuTableTile { tileId: string }
export interface DaaluuShuffleState {
  roundId: string;
  attempt: number;
  stage: DaaluuShuffleView['stage'];
  callerSeat: number;
  seats: DaaluuSeats;
  stackSize: DaaluuStackSize;
  reserveCount: number;
  tiles: HiddenTile[];
  gestures: (DaaluuGesture | null)[];
  sequences: number[];
  participatedSeats: number[];
  missingSeats: number[];
  /** Private final distribution order, preserving the spatial mixing result. */
  stacks: string[][];
}
export type DaaluuShuffleResult =
  | { ok: true; state: DaaluuShuffleState }
  | { ok: false; error: string };

export const DAALUU_SHUFFLE_MS = 10_000;
export const DAALUU_MAX_SHUFFLE_ATTEMPTS = 3;
const clamp = (value: number, margin = .04) => Math.max(margin, Math.min(1 - margin, value));
// Center spacing in table coordinates. The taller vertical gap reflects the
// portrait tile backs; it also leaves room for the small tile rotations.
const TILE_GAP_X = .045;
const TILE_GAP_Y = .075;

export function daaluuPreparationFormat(seats: DaaluuSeats, stackSize?: DaaluuStackSize) {
  if (seats !== 5) throw new Error('Daaluu requires exactly five seats.');
  const size = stackSize ?? 5;
  if (size !== 5) {
    throw new Error('Invalid Daaluu distribution stack size.');
  }
  return { playableCount: 50, reserveCount: 10, stackSize: size };
}

/** Input is the agreed inventory AFTER designated tea tiles have been reserved. */
export function createDaaluuShuffle(options: {
  seats: DaaluuSeats;
  callerSeat: number;
  stackSize?: DaaluuStackSize;
  roundId: string;
  tiles: { tileId: string; handle: string }[];
}, rng: Rng): DaaluuShuffleState {
  const format = daaluuPreparationFormat(options.seats, options.stackSize);
  if (!options.roundId || !Number.isInteger(options.callerSeat) || options.callerSeat < 0 || options.callerSeat >= options.seats) {
    throw new Error('Invalid caller or round identifier.');
  }
  if (options.tiles.length !== format.playableCount ||
      new Set(options.tiles.map(tile => tile.tileId)).size !== format.playableCount ||
      new Set(options.tiles.map(tile => tile.handle)).size !== format.playableCount ||
      options.tiles.some(tile => !tile.tileId || !tile.handle || tile.tileId === tile.handle)) {
    throw new Error('Supply a complete unique inventory with independent opaque handles.');
  }
  // Private unpredictable initialization; no further RNG is used after this step.
  const order = options.tiles.map(tile => ({ ...tile }));
  for (let i = order.length - 1; i > 0; i--) {
    const sample = rng();
    if (!Number.isFinite(sample) || sample < 0 || sample >= 1) throw new Error('Invalid injected RNG.');
    const j = Math.floor(sample * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  return {
    roundId: options.roundId, attempt: 1, stage: 'mixing',
    callerSeat: options.callerSeat, seats: options.seats, stackSize: format.stackSize,
    reserveCount: format.reserveCount,
    tiles: order.map((tile, i) => ({ ...tile,
      x: 0.12 + (i % 10) * 0.084,
      y: 0.18 + Math.floor(i / 10) * 0.13,
      angle: ((i * 37) % 40) - 20,
    })),
    gestures: Array.from({ length: options.seats }, () => null),
    sequences: Array(options.seats).fill(-1), participatedSeats: [], missingSeats: [], stacks: [],
  };
}

function distanceToSweep(tile: DaaluuPoint, from: DaaluuPoint, to: DaaluuPoint) {
  const dx = to.x - from.x, dy = to.y - from.y;
  const lengthSquared = dx * dx + dy * dy;
  const ratio = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1,
    ((tile.x - from.x) * dx + (tile.y - from.y) * dy) / lengthSquared));
  return Math.hypot(tile.x - from.x - ratio * dx, tile.y - from.y - ratio * dy);
}

/** Shared visual prediction; only the server may accept its result as state. */
export function sweepDaaluuTableTiles<T extends DaaluuTableTile>(tiles: T[], from: DaaluuPoint, to: DaaluuPoint) {
  const dx = to.x - from.x, dy = to.y - from.y;
  const selected = tiles.map(tile => distanceToSweep(tile, from, to) <= .085);
  const next = [...tiles];
  const occupied: DaaluuPoint[] = tiles.filter((_, i) => !selected[i]);
  const isFree = (point: DaaluuPoint) => occupied.every(other => {
    const x = (point.x - other.x) / TILE_GAP_X;
    const y = (point.y - other.y) / TILE_GAP_Y;
    return x * x + y * y >= 1;
  });
  // Move the leading tiles first so a sweep has room to advance. Every tile
  // then takes the nearest available position around its intended destination.
  const moving = tiles.map((_, i) => i).filter(i => selected[i])
    .sort((a, b) => (tiles[b].x - tiles[a].x) * dx + (tiles[b].y - tiles[a].y) * dy || a - b);
  let moved = false;
  for (const i of moving) {
    const tile = tiles[i];
    const target = { x: clamp(tile.x + dx * .85), y: clamp(tile.y + dy * .85, .10) };
    let spot = target;
    if (!isFree(spot)) {
      const heading = Math.atan2(dy / TILE_GAP_Y, dx / TILE_GAP_X);
      let found = false;
      for (let ring = 1; ring <= 32 && !found; ring++) {
        for (let step = 0; step < 16; step++) {
          const offset = step === 0 ? 0 : (step % 2 ? 1 : -1) * Math.ceil(step / 2);
          const angle = heading + offset * Math.PI / 8;
          const candidate = {
            x: clamp(target.x + Math.cos(angle) * ring * TILE_GAP_X / 4),
            y: clamp(target.y + Math.sin(angle) * ring * TILE_GAP_Y / 4, .10),
          };
          if (isFree(candidate)) { spot = candidate; found = true; break; }
        }
      }
      // The table has ample free area for 50 tiles; this is a deterministic
      // fallback if a dense arrangement defeats the local search.
      if (!found) {
        for (let y = .10; y <= .90 && !found; y += .025) {
          for (let x = .04; x <= .96; x += .015) {
            const candidate = { x, y };
            if (isFree(candidate)) { spot = candidate; found = true; break; }
          }
        }
      }
    }
    occupied.push(spot);
    if (spot.x !== tile.x || spot.y !== tile.y) {
      moved = true;
      next[i] = { ...tile, ...spot, angle: (tile.angle + (dx - dy) * 160) % 360 };
    }
  }
  return { tiles: next, moved };
}

/** Commands are applied serially by the room manager; there are no tile locks. */
export function applyDaaluuSweep(state: DaaluuShuffleState, seat: number, command: DaaluuShuffleCommand): DaaluuShuffleResult {
  if (state.stage !== 'mixing') return { ok: false, error: 'The shuffle is not active.' };
  if (!Number.isInteger(seat) || seat < 0 || seat >= state.seats || seat === state.callerSeat) {
    return { ok: false, error: 'The caller cannot shuffle.' };
  }
  if (command.roundId !== state.roundId || command.attempt !== state.attempt ||
      !Number.isSafeInteger(command.sequence) || command.sequence <= state.sequences[seat]) {
    return { ok: false, error: 'Stale shuffle command.' };
  }
  if (typeof command.contact !== 'boolean' || !Number.isFinite(command.x) || !Number.isFinite(command.y) ||
      command.x < 0 || command.x > 1 || command.y < 0 || command.y > 1) {
    return { ok: false, error: 'Invalid shuffle coordinates.' };
  }
  const previous = state.gestures[seat];
  const gestures = [...state.gestures];
  const sequences = [...state.sequences];
  sequences[seat] = command.sequence;
  const dx = previous ? command.x - previous.x : 0;
  const dy = previous ? command.y - previous.y : 0;
  // A jump too large for one pointer update (fast flick, dropped events, or a teleport attempt) lifts
  // the hand and sets it down at the new point without sweeping any tiles, so it is never rejected.
  const lifted = previous !== null && Math.hypot(dx, dy) > 0.4;
  const moving = command.contact && previous !== null && !lifted && Math.hypot(dx, dy) >= 0.002;
  const sweep = moving ? sweepDaaluuTableTiles(state.tiles, previous!, command) : { tiles: state.tiles, moved: false };
  gestures[seat] = command.contact
    ? { seat, x: command.x, y: command.y, angle: moving ? Math.atan2(dy, dx) * 180 / Math.PI + 90 : previous?.angle ?? 0 }
    : null;
  const participatedSeats = sweep.moved && !state.participatedSeats.includes(seat)
    ? [...state.participatedSeats, seat].sort((a, b) => a - b) : state.participatedSeats;
  return { ok: true, state: { ...state, tiles: sweep.tiles, gestures, sequences, participatedSeats } };
}

export function endDaaluuGesture(state: DaaluuShuffleState, seat: number): DaaluuShuffleState {
  if (!Number.isInteger(seat) || seat < 0 || seat >= state.seats || state.gestures[seat] === null) return state;
  const gestures = [...state.gestures];
  gestures[seat] = null;
  return { ...state, gestures };
}

/** Called at the server deadline. requiredSeats comes from current room membership. */
export function finishDaaluuShuffle(state: DaaluuShuffleState, requiredSeats: number[]): DaaluuShuffleState {
  if (state.stage !== 'mixing') return state;
  if (requiredSeats.some(seat => !Number.isInteger(seat) || seat < 0 || seat >= state.seats || seat === state.callerSeat)) {
    throw new Error('Invalid required shuffle seats.');
  }
  const missingSeats = [...new Set(requiredSeats)].filter(seat => !state.participatedSeats.includes(seat));
  if (missingSeats.length || state.participatedSeats.length === 0) {
    return { ...state, stage: 'retry', gestures: state.gestures.map(() => null), missingSeats };
  }
  // Explicit spatial order: rows top-to-bottom, then left-to-right, with stable
  // opaque handle tie-breaking. This is the final order, NOT a fresh shuffle.
  const order = [...state.tiles].sort((a, b) =>
    Math.floor(a.y * 10) - Math.floor(b.y * 10) || a.x - b.x || (a.handle < b.handle ? -1 : a.handle > b.handle ? 1 : 0));
  const stacks: string[][] = [];
  for (let i = 0; i < order.length; i += state.stackSize) {
    stacks.push(order.slice(i, i + state.stackSize).map(tile => tile.tileId));
  }
  return { ...state, stage: 'ready', gestures: state.gestures.map(() => null), missingSeats: [], stacks };
}

export function retryDaaluuShuffle(state: DaaluuShuffleState): DaaluuShuffleResult {
  if (state.stage !== 'retry' || state.attempt >= DAALUU_MAX_SHUFFLE_ATTEMPTS) {
    return { ok: false, error: 'Shuffle retry unavailable. Return to the lobby to restart.' };
  }
  return { ok: true, state: { ...state, stage: 'mixing', attempt: state.attempt + 1,
    gestures: state.gestures.map(() => null), sequences: state.sequences.map(() => -1), missingSeats: [],
  } };
}

/** Whitelist projection: never serialize hidden tile IDs or final private order. */
export function daaluuShuffleViewFor(state: DaaluuShuffleState): DaaluuShuffleView {
  return {
    roundId: state.roundId, attempt: state.attempt, stage: state.stage,
    callerSeat: state.callerSeat, seats: state.seats, stackSize: state.stackSize, reserveCount: state.reserveCount,
    tiles: state.stage === 'ready' ? [] : state.tiles.map(({ handle, x, y, angle }) => ({ handle, x, y, angle })),
    gestures: state.gestures.flatMap(gesture => gesture ? [{ ...gesture }] : []),
    participatedSeats: [...state.participatedSeats], missingSeats: [...state.missingSeats],
    acceptedSequences: [...state.sequences],
    stacks: state.stacks.map((stack, i) => ({ handle: `${state.roundId}:stack:${i}`, count: stack.length })),
  };
}
